import React, { Component } from 'react';
import { type IntlShape, injectIntl, useIntl } from '@atlassian/jira-intl';
import {
	Select as CommonSelect,
	AsyncSelect as CommonAsyncSelect,
	HoverObserver,
} from '@atlassian/jira-portfolio-3-common';
import type { Props as SelectProps } from '@atlassian/jira-portfolio-3-common/src/select/types';
import { INLINE_CREATE_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/inline-create';
import { getBody } from '@atlassian/jira-portfolio-3-portfolio/src/common/dom';

import messages from './messages';
import type { SelectForColumnCellProps, State } from './types';

export const MIN_SELECT_WIDTH = 300;

export function Select({ noOptionsMessage, ...props }: SelectProps) {
	const intl = useIntl();

	const selectProps: SelectProps = {
		...props,
		noOptionsMessage: noOptionsMessage || (() => intl.formatMessage({ ...messages.noMatchesText })),
	};
	return <CommonSelect {...selectProps} />;
}

function AsyncSelectWrapper(
	props: SelectProps & {
		intl: IntlShape;
	},
) {
	const {
		intl,
		noOptionsMessage = () => intl.formatMessage({ ...messages.noMatchesText }),
		...restProps
	} = props;

	const selectProps: SelectProps = {
		...restProps,
		noOptionsMessage,
	};
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return <CommonAsyncSelect {...(selectProps as unknown as Record<string, string>)} />;
}

// eslint-disable-next-line jira/react/no-class-components
export class SelectForColumnCell extends Component<SelectForColumnCellProps, State> {
	state = {
		// note: showPlaceholder is only used for VR test purposes in order to take a snapshot of the picklist placeholder
		isHovered: this.props.showPlaceholder || false,
		placeholder: '',
	};

	// eslint-disable-next-line react/sort-comp
	setHovered = (isHovered: boolean) => this.setState({ isHovered });

	componentWillUnmount() {
		if (this.props.issueId === INLINE_CREATE_ID) {
			this.props.lockInlineCreate(false);
		}
	}

	onMenuOpen = () => {
		if (this.props.issueId === INLINE_CREATE_ID) {
			this.props.lockInlineCreate(true);
		}
		this.setState({ placeholder: this.props.placeholder ?? '' });
		if (this.props.onMenuOpen) {
			this.props.onMenuOpen();
		}
	};

	onMenuClose = () => {
		if (this.props.issueId === INLINE_CREATE_ID) {
			this.props.lockInlineCreate(false);
		}
		this.setState({ placeholder: '' });
		if (this.props.onMenuClose) {
			this.props.onMenuClose();
		}
	};

	render() {
		const {
			issueId,
			onMenuClose,
			onMenuOpen,
			attribute,
			lockInlineCreate,
			styles,
			placeholder,
			...selectProps
		} = this.props;

		return (
			<HoverObserver onHoverChanged={this.setHovered}>
				<Select
					menuPlacement="auto"
					menuPortalTarget={getBody()}
					minSelectWidth={MIN_SELECT_WIDTH}
					onMenuClose={this.onMenuClose}
					onMenuOpen={this.onMenuOpen}
					scrollLock
					placeholder={this.state.placeholder}
					styles={{
						// eslint-disable-next-line @typescript-eslint/no-shadow
						placeholder: (styles) => ({
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							...styles,
						}),
						...styles,
					}}
					{...selectProps}
				/>
			</HoverObserver>
		);
	}
}

export default Select;

export const AsyncSelect = injectIntl(AsyncSelectWrapper);

// eslint-disable-next-line jira/export/no-external-exports
export { selectUtils } from '@atlassian/jira-portfolio-3-common';
